export const POI_MAP_SUBPAGES = new Set([
  'parking',
  'restaurants',
  'nearby-attractions',
  'neighborhood',
  'directions',
]);

export const POI_MARKER_COLORS = {
  venue: '#ff822f',
  parking: '#2563eb',
  restaurant: '#dc2626',
  attraction: '#16a34a',
  transit: '#7c3aed',
  hotel: '#0d9488',
};

export const POI_MARKER_LABELS = {
  venue: 'Venue',
  parking: 'Parking',
  restaurant: 'Restaurant',
  attraction: 'Attraction',
  transit: 'Transit',
  hotel: 'Hotel',
};

const normalizeCoord = value => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/** POI facts occasionally store non-string names (objects from manual enrichment). */
const markerLabel = (value, fallback = 'Unnamed place') => {
  if (value == null || value === '') {
    return fallback;
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (typeof value === 'object' && typeof value.name === 'string' && value.name) {
    return value.name;
  }
  return fallback;
};

/** Drop POIs that are implausibly far from the venue (stale Geoapify anchor). */
const MAX_POI_DISTANCE_FROM_VENUE_M = 2500;

const haversineMeters = (lat1, lon1, lat2, lon2) => {
  const toRad = d => (d * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const filterMarkersNearVenue = markers => {
  const venue = markers.find(marker => marker.kind === 'venue');
  if (!venue) {
    return markers;
  }
  return markers.filter(marker => {
    if (marker.kind === 'venue') {
      return true;
    }
    return haversineMeters(venue.lat, venue.lng, marker.lat, marker.lng) <= MAX_POI_DISTANCE_FROM_VENUE_M;
  });
};

export const formatPoiDistance = meters => {
  if (meters == null) {
    return null;
  }
  if (meters < 1000) {
    return `${meters} m`;
  }
  return `${(meters / 1000).toFixed(1)} km`;
};

const placeToMarker = (place, kind) => {
  const lat = normalizeCoord(place?.latitude);
  const lng = normalizeCoord(place?.longitude);
  if (lat == null || lng == null) {
    return null;
  }
  return {
    kind,
    label: markerLabel(place?.name),
    lat,
    lng,
    distanceMeters: place.distanceMeters ?? null,
  };
};

const venueMarker = venue => {
  const lat = normalizeCoord(venue?.latitude);
  const lng = normalizeCoord(venue?.longitude);
  if (lat == null || lng == null) {
    return null;
  }
  return {
    kind: 'venue',
    label: markerLabel(venue?.venueName, 'Venue'),
    lat,
    lng,
    distanceMeters: 0,
  };
};

const dedupeMarkers = markers => {
  const seen = new Set();
  return markers.filter(marker => {
    const key = `${marker.lat.toFixed(5)}:${marker.lng.toFixed(5)}:${markerLabel(marker.label).toLowerCase()}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

export const getPoiMapHeading = subpageSlug => {
  const headings = {
    parking: 'Map of nearby parking and transit',
    restaurants: 'Map of nearby restaurants',
    'nearby-attractions': 'Map of nearby attractions',
    neighborhood: 'Map of the neighborhood',
    directions: 'Map and directions',
  };
  return headings[subpageSlug] || 'Nearby map';
};

export const buildPoiMarkersForSubpage = (venue, venueFacts, subpageSlug) => {
  if (!POI_MAP_SUBPAGES.has(subpageSlug)) {
    return [];
  }

  const markers = [venueMarker(venue)].filter(Boolean);

  switch (subpageSlug) {
    case 'parking': {
      const parkingFacts = venueFacts?.parkingFacts || {};
      const directionsFacts = venueFacts?.directionsFacts || {};
      markers.push(
        ...(parkingFacts.garages || []).map(place => placeToMarker(place, 'parking')),
        ...(parkingFacts.transitStops || []).map(place => placeToMarker(place, 'transit')),
        ...(directionsFacts.transitStops || []).map(place => placeToMarker(place, 'transit'))
      );
      break;
    }
    case 'restaurants': {
      const restaurantFacts = venueFacts?.restaurantFacts || {};
      markers.push(...(restaurantFacts.places || []).map(place => placeToMarker(place, 'restaurant')));
      break;
    }
    case 'nearby-attractions': {
      const attractionFacts = venueFacts?.attractionFacts || {};
      markers.push(...(attractionFacts.places || []).map(place => placeToMarker(place, 'attraction')));
      break;
    }
    case 'neighborhood': {
      const neighborhoodFacts = venueFacts?.neighborhoodFacts || {};
      markers.push(
        ...(neighborhoodFacts.restaurants || []).map(place => placeToMarker(place, 'restaurant')),
        ...(neighborhoodFacts.attractions || []).map(place => placeToMarker(place, 'attraction')),
        ...(neighborhoodFacts.transitStops || []).map(place => placeToMarker(place, 'transit')),
        ...(neighborhoodFacts.hotels || []).map(place => placeToMarker(place, 'hotel'))
      );
      break;
    }
    case 'directions': {
      const directionsFacts = venueFacts?.directionsFacts || {};
      markers.push(
        ...(directionsFacts.transitStops || []).map(place => placeToMarker(place, 'transit')),
        ...(directionsFacts.landmarks || []).map(place => placeToMarker(place, 'attraction'))
      );
      break;
    }
    default:
      break;
  }

  return filterMarkersNearVenue(dedupeMarkers(markers.filter(Boolean)));
};

export const shouldShowPoiMap = (subpageSlug, venue, venueFacts) => {
  if (!POI_MAP_SUBPAGES.has(subpageSlug)) {
    return false;
  }
  return buildPoiMarkersForSubpage(venue, venueFacts, subpageSlug).length > 0;
};
